import { useEffect, useState, useMemo } from "react";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import PageBoundary from "../../components/PageBoundary.jsx";
import { useApp } from "../../store.jsx";
import { useRole } from "../../hooks/useRole";
import { fmtDateTime, fmtRelative } from "../../lib/dates.js";
import { listAuditLog, listAuditActors } from "../../lib/api";

const PAGE_SIZE = 50;

const RANGES = [
  [7, "7 days"],
  [30, "30 days"],
  [90, "90 days"],
];

const TARGET_TYPES = [
  "employee",
  "manager",
  "department",
  "company",
  "settings",
  "subscription",
  "announcement",
];

// Color the action badge by its verb.
const VERB_COLOR = [
  [/(deleted|removed|cancelled|canceled|deactivated)/, "badge-error"],
  [/(created|invited|added|activated)/, "badge-success"],
  [/(renamed|updated|changed|modified|reassigned)/, "badge-info"],
];
const actionColor = (a) => {
  const s = (a || "").toLowerCase();
  for (const [re, c] of VERB_COLOR) if (re.test(s)) return c;
  return "badge-muted";
};

// "employee.invited" → "Employee invited"
const humanizeAction = (a) => {
  if (!a) return "—";
  const s = String(a).replace(/[._]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function ActivityInner() {
  const { toast, openModal, closeModal } = useApp();
  const role = useRole();
  const isOwner = role === "owner";

  const [resp, setResp] = useState({ items: [], total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [search, setSearch] = useState("");
  const [actor, setActor] = useState("");
  const [targetType, setTargetType] = useState("");
  const [actors, setActors] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isOwner) return; // managers only ever see their own entries
    listAuditActors().then(setActors).catch(() => setActors([]));
  }, [isOwner]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) qs.set("from", dateFrom);
      if (dateTo) qs.set("to", dateTo);
    } else {
      qs.set("range", String(range));
    }
    if (actor) qs.set("actor", actor);
    if (targetType) qs.set("targetType", targetType);
    qs.set("page", String(page));
    qs.set("limit", String(PAGE_SIZE));
    listAuditLog(`?${qs}`)
      .then((d) => setResp(d || { items: [], total: 0, hasMore: false }))
      .catch((e) => toast(e.message || "Failed to load activity log", "error"))
      .finally(() => setLoading(false));
  }, [range, dateFrom, dateTo, actor, targetType, page, toast]);

  useEffect(() => {
    setPage(1);
  }, [range, dateFrom, dateTo, actor, targetType, search]);

  const filtered = useMemo(
    () =>
      resp.items.filter((l) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (l.action || "").toLowerCase().includes(q) ||
          (l.targetType || "").toLowerCase().includes(q) ||
          (l.targetLabel || "").toLowerCase().includes(q) ||
          (l.actorName || "").toLowerCase().includes(q) ||
          (l.actorEmail || "").toLowerCase().includes(q)
        );
      }),
    [resp.items, search],
  );

  const totalPages = Math.max(1, Math.ceil(resp.total / PAGE_SIZE));

  const openDetail = (l) => {
    const meta = l.metadata;
    const metaEntries = meta && typeof meta === "object" ? Object.entries(meta) : [];
    openModal(
      <>
        <div className="modal-head">
          <h3>
            <span className={`badge ${actionColor(l.action)}`} style={{ marginRight: 8 }}>
              {humanizeAction(l.action)}
            </span>
            Activity detail
          </h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <div style={{ padding: 20, borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "10px 0", fontSize: 13 }}>
              <MetaLabel>When</MetaLabel>
              <div>{fmtDateTime(l.createdAt)}</div>

              <MetaLabel>Who</MetaLabel>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Avatar name={l.actorName || l.actorEmail || "?"} size="sm" seed={(l.actorEmail || "").length} />
                <div>
                  <div style={{ fontWeight: 500 }}>{l.actorName || "—"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{l.actorEmail}</div>
                </div>
              </div>

              <MetaLabel>Target</MetaLabel>
              <div>
                <span className="badge badge-muted" style={{ marginRight: 6 }}>{l.targetType}</span>
                {l.targetLabel && <span style={{ fontWeight: 500 }}>{l.targetLabel}</span>}
                {l.targetId && (
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    {l.targetId}
                  </div>
                )}
              </div>

              {l.ip && (
                <>
                  <MetaLabel>IP address</MetaLabel>
                  <div style={{ fontFamily: "monospace", fontSize: 12 }}>{l.ip}</div>
                </>
              )}
            </div>
          </div>

          {l.userAgent && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                Device
              </div>
              <div style={{ fontSize: 12, color: "var(--text)", wordBreak: "break-all" }}>{l.userAgent}</div>
            </div>
          )}

          {metaEntries.length > 0 && (
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
                Details
              </div>
              <div style={{ background: "var(--surface-2, rgba(148,163,184,0.06))", borderRadius: 8, border: "1px solid var(--border)", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <tbody>
                    {metaEntries.map(([key, val], idx) => (
                      <tr key={key} style={{ borderBottom: idx < metaEntries.length - 1 ? "1px solid var(--border)" : "none" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap", width: 140, verticalAlign: "top", fontFamily: "monospace", fontSize: 11 }}>
                          {key}
                        </td>
                        <td style={{ padding: "8px 12px", color: "var(--text)", wordBreak: "break-word" }}>
                          {renderMetaValue(val)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Close</button>
        </div>
      </>,
      "lg",
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Activity Log</h1>
          <p className="page-sub">
            {isOwner
              ? "Every change made in your workspace, recorded"
              : "Your activity — every change you've made"}
            {" · "}
            {resp.total} {resp.total === 1 ? "entry" : "entries"}
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14, padding: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <Icon name="search" size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input
                className="input"
                placeholder="Search action, target, person…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 30, height: 34, fontSize: 13 }}
              />
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {RANGES.map(([k, l]) => (
                <button
                  key={k}
                  className={`filter-chip ${range === k && !dateFrom && !dateTo ? "active" : ""}`}
                  onClick={() => { setRange(k); setDateFrom(""); setDateTo(""); }}
                  style={{ fontSize: 12, padding: "5px 10px" }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {isOwner && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>Person</span>
                <select className="input" value={actor} onChange={(e) => setActor(e.target.value)} style={{ height: 30, fontSize: 12, padding: "0 6px", width: 150 }}>
                  <option value="">Everyone</option>
                  {actors.map((a) => (
                    <option key={a.id} value={a.id}>{a.name || a.email}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>Type</span>
              <select className="input" value={targetType} onChange={(e) => setTargetType(e.target.value)} style={{ height: 30, fontSize: 12, padding: "0 6px", width: 130 }}>
                <option value="">All</option>
                {TARGET_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>From</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                style={{ height: 30, fontSize: 12, padding: "0 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: 130 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>To</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                style={{ height: 30, fontSize: 12, padding: "0 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: 130 }} />
            </div>
            {(dateFrom || dateTo) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setDateFrom(""); setDateTo(""); setRange(30); }} style={{ height: 30 }}>
                <Icon name="x" size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
              <div className="skeleton-line" style={{ width: 90, height: 12, borderRadius: 4 }} />
              <div className="skeleton-line" style={{ width: 32, height: 32, borderRadius: "50%" }} />
              <div className="skeleton-line" style={{ flex: 1, height: 12, borderRadius: 4 }} />
              <div className="skeleton-line" style={{ width: 70, height: 18, borderRadius: 9 }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No activity yet" desc="Changes you make in the workspace will show up here." icon="history" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>When</th>
                {isOwner && <th>Who</th>}
                <th style={{ width: 150 }}>Action</th>
                <th>Target</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} onClick={() => openDetail(l)} style={{ cursor: "pointer" }}>
                  <td data-label="When" style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                    <span title={fmtDateTime(l.createdAt)}>{fmtRelative(l.createdAt)}</span>
                  </td>
                  {isOwner && (
                    <td data-label="Who">
                      <div className="cell-emp">
                        <Avatar name={l.actorName || l.actorEmail || "?"} size="sm" seed={(l.actorEmail || "").length} />
                        <div>
                          <div className="name">{l.actorName || "—"}</div>
                          <div className="sub">{l.actorEmail}</div>
                        </div>
                      </div>
                    </td>
                  )}
                  <td data-label="Action">
                    <span className={`badge ${actionColor(l.action)}`} style={{ fontSize: 11 }}>{humanizeAction(l.action)}</span>
                  </td>
                  <td data-label="Target">
                    <span className="badge badge-muted" style={{ fontSize: 11, marginRight: 6 }}>{l.targetType}</span>
                    {l.targetLabel || <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td>
                    <Icon name="chevron-right" size={14} style={{ color: "var(--text-muted)" }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && resp.total > 0 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <Icon name="chevron-left" size={14} /> Prev
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Page {page} of {totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={!resp.hasMore} onClick={() => setPage((p) => p + 1)}>
            Next <Icon name="chevron-right" size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

function MetaLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
      {children}
    </div>
  );
}

function renderMetaValue(val) {
  if (val === null || val === undefined) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  if (typeof val === "boolean")
    return <span className={`badge ${val ? "badge-success" : "badge-muted"}`} style={{ fontSize: 11 }}>{String(val)}</span>;
  if (Array.isArray(val)) return val.length ? val.join(", ") : <span style={{ color: "var(--text-muted)" }}>—</span>;
  if (typeof val === "object")
    return (
      <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.4, color: "var(--text-muted)", maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap" }}>
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  return String(val);
}

export default function ActivityLog() {
  return (
    <PageBoundary>
      <ActivityInner />
    </PageBoundary>
  );
}
